import type {DiffAnchor} from '@github-ui/diffs/types'
import {Fragment, memo, type MouseEvent, type RefObject, useEffect, useMemo} from 'react'

import {DiffLineContextProvider} from '../contexts/DiffLineContext'
import type {
  ReplaceSelectedDiffRowRangeFromGridCellsFnArgs,
  UpdateSelectedDiffRowRangeFnArgs,
} from '../contexts/SelectedDiffRowRangeContext'
import {threadSummary} from '../helpers/conversation-helpers'
import DiffRowSelectedChecker from '../helpers/diff-row-selected-checker'
import {getDiffHunksData, getLineHunkData} from '../helpers/hunk-data-helpers'
import {isEmptyDiffLine, isDiffLine, rowIdFrom} from '../helpers/line-helpers'
import {useGridNavigation} from '../hooks/use-grid-navigation'
import type {DiffLine, ClientDiffLine, LineRange} from '../types'
import {EMPTY_DIFF_LINE} from '../types'
import DiffLineSummary from './DiffLineSummary'
import {DiffLineSide} from './DiffLineTableRow'
import ExpandableHunkHeaderDiffLine from './ExpandableHunkHeaderDiffLine'

/**
 * Split a context line into two lines, one for each side of the diff. The only divergence will be the annotations and
 * threads data, which will be filtered to only include threads for the respective diff side and only include
 * annotations on the right side.
 */
function splitContextLine(line: DiffLine): [DiffLine, DiffLine] {
  const leftLineThreads = line.threadsData?.threads?.filter(thread => thread?.diffSide === 'LEFT') ?? null
  const leftLine: DiffLine = {
    ...line,
    threadsData: {
      ...line.threadsData,
      threads: leftLineThreads,
      totalCommentsCount:
        leftLineThreads?.reduce((acc, thread) => acc + (thread?.commentsData?.totalCount ?? 0), 0) ?? 0,
      totalCount: leftLineThreads?.length ?? 0,
    },
    annotationsData: {
      totalCount: 0,
      annotations: [],
    },
  }
  const rightLineThreads = line.threadsData?.threads?.filter(thread => thread?.diffSide === 'RIGHT') ?? null
  const rightLine: DiffLine = {
    ...line,
    threadsData: {
      ...line.threadsData,
      threads: rightLineThreads,
      totalCommentsCount:
        rightLineThreads?.reduce((acc, thread) => acc + (thread?.commentsData?.totalCount ?? 0), 0) ?? 0,
      totalCount: rightLineThreads?.length ?? 0,
    },
  }
  return [leftLine, rightLine]
}

function groupDiffLines(lines: DiffLine[]): {
  leftLines: ClientDiffLine[]
  rightLines: ClientDiffLine[]
} {
  const leftLines: ClientDiffLine[] = []
  const rightLines: ClientDiffLine[] = []

  // Keep list of lines even, filling in blanks as needed
  const fillBlanks = () => {
    while (leftLines.length < rightLines.length) {
      leftLines.push(EMPTY_DIFF_LINE)
    }

    while (rightLines.length < leftLines.length) {
      rightLines.push(EMPTY_DIFF_LINE)
    }
  }

  for (const line of lines) {
    switch (line.type) {
      case 'ADDITION':
        rightLines.push(line)
        break
      case 'DELETION':
        leftLines.push(line)
        break
      case 'CONTEXT':
      case 'INJECTED_CONTEXT':
        fillBlanks()
        // eslint-disable-next-line no-case-declarations
        const [leftLine, rightLine] = splitContextLine(line)
        leftLines.push(leftLine)
        rightLines.push(rightLine)
        break
      case 'HUNK':
        fillBlanks()
        leftLines.push(line)
        rightLines.push(line)
    }
  }
  fillBlanks()

  return {leftLines, rightLines}
}

const SplitDiffLines = memo(function SplitDiffLines({
  clearSelectedDiffRowRange,
  diffEntryId,
  diffLines,
  fileAnchor,
  fileLineCount,
  filePath,
  handleDiffRowClick,
  handleDiffSideCellSelectionBlocking,
  replaceSelectedDiffRowRangeFromGridCells,
  selectedDiffRowRange,
  tableRef,
  updateDiffLines,
  updateSelectedDiffRowRange,
}: {
  clearSelectedDiffRowRange: () => void
  diffEntryId: string
  diffLines: DiffLine[]
  fileAnchor: DiffAnchor
  fileLineCount: number
  filePath: string
  handleDiffRowClick: (
    event: MouseEvent<HTMLTableCellElement>,
    lineNumber: number,
    orientation: 'left' | 'right' | undefined,
    shiftKey: boolean,
  ) => void
  handleDiffSideCellSelectionBlocking: (diffSide: 'left' | 'right') => void
  replaceSelectedDiffRowRangeFromGridCells: ReplaceSelectedDiffRowRangeFromGridCellsFnArgs
  selectedDiffRowRange: LineRange | null
  tableRef: RefObject<HTMLTableElement>
  updateDiffLines: (diffAnchor: string, diffLines: DiffLine[]) => void
  updateSelectedDiffRowRange: UpdateSelectedDiffRowRangeFnArgs
}) {
  const {leftLines, rightLines} = useMemo(() => groupDiffLines(diffLines), [diffLines])

  useGridNavigation({
    clearSelectedDiffRowRange,
    containerRef: tableRef,
    fileAnchor,
    isSplitDiff: true,
    leftLines,
    replaceSelectedDiffRowRangeFromGridCells,
    rightLines,
    selectedDiffRowRange,
    updateSelectedDiffRowRange,
  })

  const diffRowSelectedChecker = useMemo(() => {
    if (!selectedDiffRowRange) return undefined
    return DiffRowSelectedChecker({
      selectedDiffRowRange,
      leftLines,
      rightLines,
    })
  }, [selectedDiffRowRange, leftLines, rightLines])

  useEffect(() => {
    updateDiffLines(fileAnchor, diffLines)
  }, [diffLines, fileAnchor, updateDiffLines])

  const hunksData = useMemo(() => getDiffHunksData(leftLines), [leftLines])

  const lines: React.ReactNode[] = []
  for (let i = 0; i < leftLines.length; i++) {
    const leftLine = leftLines[i] as ClientDiffLine
    const rightLine = rightLines[i] as ClientDiffLine
    const prevLine = leftLines[i - 1]

    // Only need to use next right line when next left line is empty
    const nextLine = (isEmptyDiffLine(leftLines[i + 1]) ? rightLines[i + 1] : leftLines[i + 1]) as DiffLine | undefined
    const isHunkRow = isDiffLine(leftLine) && leftLine.type === 'HUNK'
    const rowId = rowIdFrom(fileAnchor, leftLine, rightLine)
    const currentLine = !isEmptyDiffLine(leftLine) ? leftLine : !isEmptyDiffLine(rightLine) ? rightLine : undefined

    if (!currentLine) continue

    const originalLineThreads = !isEmptyDiffLine(leftLine) ? leftLine.threadsData : undefined
    const modifiedLineThreads = !isEmptyDiffLine(rightLine) ? rightLine.threadsData : undefined

    const {currentHunk, nextHunk, previousHunk} = getLineHunkData(currentLine, hunksData)

    const diffLineContextData = {
      currentHunk,
      diffEntryId,
      fileAnchor,
      fileLineCount,
      filePath,
      isRowSelected:
        diffRowSelectedChecker?.isRowSelected(leftLine) || diffRowSelectedChecker?.isRowSelected(rightLine),
      modifiedLineThreads: threadSummary(modifiedLineThreads),
      modifiedLineThreadsConnectionId: modifiedLineThreads?.__id,
      nextHunk,
      originalLineThreads: threadSummary(originalLineThreads),
      originalLineThreadsConnectionId: originalLineThreads?.__id,
      previousHunk,
      rowId,
      isSplit: true,
    }

    lines.push(
      <Fragment key={rowId}>
        <tr className="diff-line-row">
          {isHunkRow ? (
            <DiffLineContextProvider
              {...{
                ...diffLineContextData,
                diffLine: currentLine,
              }}
            >
              <ExpandableHunkHeaderDiffLine nextLine={nextLine} prevLine={prevLine as DiffLine} />
            </DiffLineContextProvider>
          ) : (
            <>
              <DiffLineContextProvider
                {...{
                  ...diffLineContextData,
                  diffLine: leftLine,
                  isLeftSide: true,
                }}
              >
                <DiffLineSide
                  fileAnchor={fileAnchor}
                  filePath={filePath}
                  handleDiffRowClick={handleDiffRowClick}
                  handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
                  diffLineSummary={
                    <DiffLineSummary
                      currentSide="LEFT"
                      leftLine={leftLine as DiffLine}
                      rightLine={rightLine as DiffLine}
                    />
                  }
                />
              </DiffLineContextProvider>
              <DiffLineContextProvider
                {...{
                  ...diffLineContextData,
                  diffLine: rightLine,
                }}
              >
                <DiffLineSide
                  fileAnchor={fileAnchor}
                  filePath={filePath}
                  handleDiffRowClick={handleDiffRowClick}
                  handleDiffSideCellSelectionBlocking={handleDiffSideCellSelectionBlocking}
                  diffLineSummary={
                    <DiffLineSummary
                      currentSide="RIGHT"
                      leftLine={leftLine as DiffLine}
                      rightLine={rightLine as DiffLine}
                    />
                  }
                />
              </DiffLineContextProvider>
            </>
          )}
        </tr>
      </Fragment>,
    )
  }

  return <>{lines}</>
})

export default SplitDiffLines

try{ SplitDiffLines.displayName ||= 'SplitDiffLines' } catch {}